import { useCallback, useState } from 'react';
import Collapse from './Collapse';
import Flex, { Center, ColumnFlex, Props as FlexProps } from './Flex';
import { IconPlus } from './icons';
import ExpandIconButton from './input/ExpandIconButton';
import Card from './surface/Card';
import StyledText from './text/StyledText';

export type AccordionItemOption = {
  header?: React.ReactNode;
  body?: React.ReactNode;
};
type AccordionItemProps = AccordionItemOption & {
  isOpen?: boolean;
  onClick?: () => void;
};

export interface Props {
  width?: FlexProps['$width'];
  maxWidth?: FlexProps['$maxWidth'];
  items?: AccordionItemOption[];
}

function AccordionItem({
  isOpen,
  header,
  body,
  onClick: handleClick,
}: AccordionItemProps) {
  return (
    <Card
      $rounded="xs"
      $borderWidth={1}
      $borderColor={isOpen ? 'transparent' : undefined}
      $bg={isOpen ? undefined : 'transparent'}
      $shadow={isOpen ? undefined : 'unset'}
    >
      <Collapse
        padding="md"
        icon=""
        isOpen={isOpen}
        header={
          <Center $gap="xxs">
            <ExpandIconButton
              value={isOpen}
              size={16}
              rotateDegree={45}
              icon={IconPlus}
            />
            <StyledText
              $fontSize="sm"
              $fontWeight="bold"
              $color={isOpen ? undefined : 'secondary'}
            >
              {header}
            </StyledText>
          </Center>
        }
        onHeaderClick={handleClick}
      >
        <Flex $padding={{ left: 'xl' }}>
          <StyledText $fontSize="xs" $fontWeight="regular" $color="secondary">
            {body}
          </StyledText>
        </Flex>
      </Collapse>
    </Card>
  );
}

export default function Accordion({ items, width, maxWidth }: Props) {
  // state
  const [value, setValue] = useState(-1);

  // callback
  const handleClick = useCallback(
    (index: number) => () =>
      setValue((newValue) => (newValue === index ? -1 : index)),
    []
  );

  return (
    <ColumnFlex $gap="xs" $width={width} $maxWidth={maxWidth}>
      {items?.map((item, index) => (
        <AccordionItem
          {...item}
          isOpen={value === index}
          onClick={handleClick(index)}
          key={index}
        />
      ))}
    </ColumnFlex>
  );
}
